import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import type { TrainerChecklistPhoto } from '@/lib/trainerChecklistReviews'
import type { TrainerReviewTeacherOption } from '@/services/franchiseTeacherQualityService'

export interface SeniorTeacherReviewCity {
  cityId: number
  cityName: string
}

export interface SeniorTeacherReviewTeacherOption extends TrainerReviewTeacherOption {
  cityIds: number[]
}

export interface SeniorTeacherReviewSession {
  itemId: string
  teacherId: string
  teacherName: string
  teacherStatus: 'working' | 'training'
  studioInternalId: number
  studioNumber: number | null
  studioAddress: string
  cityId: number | null
  cityName: string
  trainingDate: string
  trainingTime: string
  directionId: string
  directionName: string
}

export interface SeniorTeacherReviewContext {
  partnerNumber: string
  reviewerTeacherId: string
  teachers: SeniorTeacherReviewTeacherOption[]
  cities: SeniorTeacherReviewCity[]
  sessions: SeniorTeacherReviewSession[]
}


export interface SeniorTeacherManualReviewStudio {
  studioInternalId: number
  studioNumber: string | null
  cityId: number | null
  cityName: string
  address: string
}

export interface SeniorTeacherManualReviewDirection {
  directionId: string
  name: string
}

export interface SeniorTeacherManualReviewOptions {
  studios: SeniorTeacherManualReviewStudio[]
  directions: SeniorTeacherManualReviewDirection[]
}

export interface SeniorTeacherReviewEditAccess {
  reviewId: string
  reviewKey: string
  teacherId: string
}

export interface SaveSeniorTeacherReviewResult {
  reviewId: string
  reviewKey: string
  teacherId: string
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const value = error as { message?: string; details?: string; hint?: string; code?: string }
    if (value.code === '57014') return 'Тренировки не успели загрузиться. Выберите педагога ещё раз или повторите попытку.'
    return [value.message, value.details, value.hint, value.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function asRows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter(item => item != null && typeof item === 'object') as Array<Record<string, unknown>>
    : []
}

function text(value: unknown): string {
  return value == null ? '' : String(value)
}

function sanitizeFileName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'photo'
}

export async function getSeniorTeacherReviewContext(
  studioInternalId: number
): Promise<SeniorTeacherReviewContext> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_senior_teacher_review_context', {
      p_studio_internal_id: studioInternalId,
    })
  )
  if (error) throw new Error(formatError(error))

  const payload = asRecord(data)
  return {
    partnerNumber: text(payload.partner_number),
    reviewerTeacherId: text(payload.reviewer_teacher_id),
    teachers: asRows(payload.teachers).map(row => ({
      teacherId: text(row.teacher_id),
      fullName: text(row.full_name),
      status: row.status === 'training' ? 'training' : 'working',
      partnerNumbers: [],
      citiesText: text(row.cities_text),
      cityIds: Array.isArray(row.city_ids)
        ? row.city_ids.map(value => Number(value)).filter(Number.isFinite)
        : [],
    })),
    cities: asRows(payload.cities).map(row => ({
      cityId: Number(row.city_id),
      cityName: text(row.city_name),
    })),
    sessions: asRows(payload.sessions).map(row => ({
      itemId: text(row.item_id),
      teacherId: text(row.teacher_id),
      teacherName: text(row.teacher_name),
      teacherStatus: row.teacher_status === 'training' ? 'training' : 'working',
      studioInternalId: Number(row.studio_internal_id),
      studioNumber: row.studio_number == null ? null : Number(row.studio_number),
      studioAddress: text(row.studio_address),
      cityId: row.city_id == null ? null : Number(row.city_id),
      cityName: text(row.city_name),
      trainingDate: text(row.training_date),
      trainingTime: text(row.training_time),
      directionId: row.direction_id == null ? '' : String(row.direction_id),
      directionName: text(row.direction_name),
    })),
  }
}

export async function getSeniorTeacherReviewSessions(
  studioInternalId: number,
  teacherId: string
): Promise<SeniorTeacherReviewSession[]> {
  if (!teacherId) return []
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_senior_teacher_review_sessions', {
      p_studio_internal_id: studioInternalId,
      p_teacher_id: teacherId,
    })
  )
  if (error) throw new Error(formatError(error))

  return asRows(data).map(row => ({
    itemId: text(row.item_id),
    teacherId: text(row.teacher_id),
    teacherName: text(row.teacher_name),
    teacherStatus: row.teacher_status === 'training' ? 'training' : 'working',
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: row.studio_number == null ? null : Number(row.studio_number),
    studioAddress: text(row.studio_address),
    cityId: row.city_id == null ? null : Number(row.city_id),
    cityName: text(row.city_name),
    trainingDate: text(row.training_date),
    trainingTime: text(row.training_time),
    directionId: row.direction_id == null ? '' : String(row.direction_id),
    directionName: text(row.direction_name),
  }))
}

export async function getSeniorTeacherManualReviewOptions(
  studioInternalId: number
): Promise<SeniorTeacherManualReviewOptions> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_senior_teacher_manual_review_options', {
      p_studio_internal_id: studioInternalId,
    })
  )
  if (error) throw new Error(formatError(error))

  const payload = asRecord(data)
  return {
    studios: asRows(payload.studios).map(row => ({
      studioInternalId: Number(row.studio_internal_id),
      studioNumber: row.studio_number == null ? null : String(row.studio_number),
      cityId: row.city_id == null ? null : Number(row.city_id),
      cityName: text(row.city_name),
      address: text(row.address),
    })),
    directions: asRows(payload.directions).map(row => ({
      directionId: text(row.direction_id),
      name: text(row.name),
    })),
  }
}

export async function getSeniorTeacherReviewEditAccess(
  studioInternalId: number,
  reviewKey: string
): Promise<SeniorTeacherReviewEditAccess | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_senior_teacher_review_edit_access', {
      p_studio_internal_id: studioInternalId,
      p_review_key: reviewKey,
    })
  )
  if (error) throw new Error(formatError(error))

  const row = asRows(data)[0]
  if (!row) return null
  return {
    reviewId: text(row.review_id),
    reviewKey: text(row.review_key),
    teacherId: text(row.teacher_id),
  }
}

export async function saveSeniorTeacherManualReview(input: {
  studioInternalId: number
  teacherId: string
  targetStudioInternalId: number
  trainingDate: string
  trainingTime: string
  directionId?: string | null
  directionName?: string | null
  generalComment?: string | null
  scores: Array<{ criterionCode: string; score: number | null; comment?: string | null }>
}): Promise<SaveSeniorTeacherReviewResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('save_senior_teacher_manual_review', {
      p_studio_internal_id: input.studioInternalId,
      p_teacher_id: input.teacherId,
      p_target_studio_internal_id: input.targetStudioInternalId,
      p_training_date: input.trainingDate,
      p_training_time: input.trainingTime,
      p_direction_id: input.directionId ? Number(input.directionId) : null,
      p_direction_name: input.directionName?.trim() || null,
      p_general_comment: input.generalComment ?? null,
      p_scores: input.scores.map(score => ({
        criterion_code: score.criterionCode,
        score: score.score,
        comment: score.comment ?? null,
      })),
    })
  )
  if (error) throw new Error(`Не удалось сохранить senior-отсмотр: ${formatError(error)}`)

  const payload = asRecord(data)
  const result = {
    reviewId: text(payload.review_id),
    reviewKey: text(payload.review_key),
    teacherId: text(payload.teacher_id),
  }
  if (!result.reviewId || !result.reviewKey || !result.teacherId) {
    throw new Error('Сервер не вернул сохранённый senior-отсмотр')
  }
  return result
}

export async function saveSeniorTeacherReview(input: {
  studioInternalId: number
  itemId?: string | null
  reviewKey?: string | null
  generalComment?: string | null
  scores: Array<{ criterionCode: string; score: number | null; comment?: string | null }>
}): Promise<SaveSeniorTeacherReviewResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('save_senior_teacher_review', {
      p_studio_internal_id: input.studioInternalId,
      p_item_id: input.itemId ? Number(input.itemId) : null,
      p_review_key: input.reviewKey || null,
      p_general_comment: input.generalComment ?? null,
      p_scores: input.scores.map(score => ({
        criterion_code: score.criterionCode,
        score: score.score,
        comment: score.comment ?? null,
      })),
    })
  )
  if (error) throw new Error(`Не удалось сохранить senior-отсмотр: ${formatError(error)}`)

  const payload = asRecord(data)
  const result = {
    reviewId: text(payload.review_id),
    reviewKey: text(payload.review_key),
    teacherId: text(payload.teacher_id),
  }
  if (!result.reviewId || !result.reviewKey || !result.teacherId) {
    throw new Error('Сервер не вернул сохранённый senior-отсмотр')
  }
  return result
}

export async function uploadSeniorTeacherReviewPhoto(params: {
  reviewId: string
  reviewKey: string
  reviewScoreId?: string | null
  photo: TrainerChecklistPhoto
  sortOrder: number
}): Promise<TrainerChecklistPhoto> {
  const { reviewId, reviewKey, reviewScoreId, photo, sortOrder } = params
  if (!photo.file) return photo

  const supabase = getSupabase()
  const extension = photo.file.name.includes('.') ? photo.file.name.split('.').pop() : 'jpg'
  const storagePath = `${reviewId}/${Date.now()}-${Math.random().toString(16).slice(2)}-${sanitizeFileName(photo.file.name || `photo.${extension}`)}`

  const { error: uploadError } = await withAuthRecovery(() =>
    supabase.storage.from('trainer-review-photos').upload(storagePath, photo.file!, {
      upsert: false,
      contentType: photo.file!.type || undefined,
    })
  )
  if (uploadError) {
    throw new Error(`Не удалось загрузить файл в хранилище: ${formatError(uploadError)}`)
  }

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_senior_teacher_review_photo', {
      p_review_key: reviewKey,
      p_review_score_id: reviewScoreId ? Number(reviewScoreId) : null,
      p_storage_path: storagePath,
      p_original_name: photo.name,
      p_mime_type: photo.file?.type || photo.mimeType || null,
      p_size_bytes: photo.file?.size ?? photo.sizeBytes ?? null,
      p_width_px: photo.widthPx ?? null,
      p_height_px: photo.heightPx ?? null,
      p_sort_order: sortOrder,
    })
  )
  if (error) {
    await supabase.storage.from('trainer-review-photos').remove([storagePath])
    throw new Error(`Файл загрузился, но не привязался к отсмотру: ${formatError(error)}`)
  }

  return {
    ...photo,
    id: `photo-${data}`,
    photoId: Number(data),
    reviewScoreId: reviewScoreId ? Number(reviewScoreId) : undefined,
    storageBucket: 'trainer-review-photos',
    storagePath,
    file: undefined,
  }
}

export async function deleteSeniorTeacherReviewPhoto(photo: TrainerChecklistPhoto): Promise<void> {
  if (photo.photoId == null) return

  const supabase = getSupabase()
  if (photo.storageBucket && photo.storagePath) {
    const { error: storageError } = await withAuthRecovery(() =>
      supabase.storage.from(photo.storageBucket!).remove([photo.storagePath!])
    )
    if (storageError) throw new Error(formatError(storageError))
  }

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('delete_senior_teacher_review_photo', { p_photo_id: photo.photoId })
  )
  if (error) throw new Error(formatError(error))
  if (!data) throw new Error('Фото не найдено или больше недоступно для редактирования')
}
